import deMessages from '@locales/de.json'
import enMessages from '@locales/en.json'
import type { ReactNode } from 'react';
import { createContext, useContext, useState } from 'react'
import { IntlProvider } from 'react-intl'

// Subpath, not the package barrel: the barrel re-exports DataTable and the
// other shared components, which would drag @tanstack/react-table and emotion
// into this app's production bundle for the sake of one type guard.
import { isOneOf } from '@hrworks/custom/type-guards'

import type { I18nContextValue, SupportedLocale } from './I18nContext.types'

const messages = {
  en: enMessages,
  de: deMessages,
}

const SUPPORTED_LOCALES: readonly SupportedLocale[] = ['en', 'de']

// `messages[locale]` must never resolve to `undefined` — that leaves react-intl
// with no message catalog and no id to fall back to, so any `formatMessage`
// call (including the app's own crash-screen fallback) renders empty. A stale
// or foreign `localStorage` value is a real, unguarded path here otherwise.
const isSupportedLocale = (value: string | null): value is SupportedLocale =>
  value !== null && isOneOf(value, SUPPORTED_LOCALES)

const I18nContext = createContext<I18nContextValue | undefined>(undefined)

export interface I18nProviderProps {
  children: ReactNode
}

export const I18nProvider = ({ children }: I18nProviderProps) => {
  // Initialize locale from saved preference or browser language
  const getInitialLocale = (): SupportedLocale => {
    const savedLang = localStorage.getItem('locale')
    if (isSupportedLocale(savedLang)) {
      return savedLang
    }
    const browserLang = navigator.language.split('-')[0]
    return browserLang === 'de' ? 'de' : 'en'
  }

  const [locale, setLocale] = useState<SupportedLocale>(() => getInitialLocale())

  const changeLocale = (newLocale: SupportedLocale) => {
    setLocale(newLocale)
    localStorage.setItem('locale', newLocale)
  }

  const contextValue: I18nContextValue = {
    locale,
    changeLocale,
  }

  return (
    <I18nContext.Provider value={contextValue}>
      <IntlProvider locale={locale} messages={messages[locale]} defaultLocale="en">
        {children}
      </IntlProvider>
    </I18nContext.Provider>
  )
}

export const useI18n = (): I18nContextValue => {
  const context = useContext(I18nContext)
  if (!context) {
    throw new Error('useI18n must be used within an I18nProvider')
  }
  return context
}
